"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { DndContext, DragOverlay, pointerWithin, PointerSensor, useSensor, useSensors, useDroppable, DragStartEvent, DragEndEvent, DragOverEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import type { FolderWithChildren, Thread } from "@/types/chat";
import { FolderItem } from "./FolderItem";
import { ThreadItem } from "./ThreadItem";
type FolderTreeProps = {
  folders: FolderWithChildren[];
  threads: Thread[]; // Threads without folders (root level)
  selectedThreadId: string | null;
  onSelectThread: (id: string) => void;
  onDeleteThread: (id: string) => Promise<void>;
  onUpdateThreadTitle: (id: string, newTitle: string) => Promise<void>;
  onCreateFolder: (name: string, parentId?: string | null) => Promise<any>;
  onUpdateFolder: (id: string, updates: {
    name?: string;
    color?: string;
    is_collapsed?: boolean;
  }) => Promise<void>;
  onDeleteFolder: (id: string) => Promise<void>;
  onMoveFolder: (folderId: string, newParentId: string | null) => Promise<void>;
  onMoveThread: (threadId: string, folderId: string | null) => Promise<void>;
  onBulkMoveThreads: (threadIds: string[], folderId: string | null) => Promise<void>;
  onToggleFolderCollapse: (folderId: string) => Promise<void>;
  // Context panel props
  threadContextIds?: Set<string>;
  onAddThreadToContext?: (threadId: string, threadTitle: string) => void;
  // Thread info prop
  onShowThreadInfo?: (threadId: string) => void;
};
type DragItem = {
  type: "folder" | "thread";
  id: string;
};
type DropIndicatorState = {
  folderName: string;
  x: number;
  y: number;
  count?: number;
  itemType?: "folder" | "thread";
} | null;
export function FolderTree(t0) {
  const $ = _c(121);
  const {
    folders,
    threads,
    selectedThreadId,
    onSelectThread,
    onDeleteThread,
    onUpdateThreadTitle,
    onCreateFolder,
    onUpdateFolder,
    onDeleteFolder,
    onMoveFolder,
    onMoveThread,
    onBulkMoveThreads,
    onToggleFolderCollapse,
    threadContextIds,
    onAddThreadToContext,
    onShowThreadInfo
  } = t0;
  const [activeItem, setActiveItem] = useState(null);
  const [overId, setOverId] = useState(null);
  const [dropIndicator, setDropIndicator] = useState(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderParentId, setNewFolderParentId] = useState(null);
  const newFolderInputRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [multiSelectedIds, setMultiSelectedIds] = useState(t1);
  const [lastClickedThreadId, setLastClickedThreadId] = useState(null);
  let result;
  if ($[1] !== folders || $[2] !== threads) {
    result = [];
    const collectFromFolder = folder => {
      if (!folder.is_collapsed) {
        folder.threads.forEach(t => result.push(t));
        folder.children.forEach(collectFromFolder);
      }
    };
    folders.forEach(collectFromFolder);
    threads.forEach(t_0 => result.push(t_0));
    $[1] = folders;
    $[2] = threads;
    $[3] = result;
  } else {
    result = $[3];
  }
  const allThreadsInOrder = result;
  let findFolderName;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    findFolderName = (id, folderList) => {
      for (const folder_0 of folderList) {
        if (folder_0.id === id) {
          return folder_0.name;
        }
        const found = findFolderName(id, folder_0.children);
        if (found) {
          return found;
        }
      }
      return null;
    };
    $[4] = findFolderName;
  } else {
    findFolderName = $[4];
  }
  const isDescendantOf = _temp;
  let t2;
  if ($[5] !== allThreadsInOrder || $[6] !== lastClickedThreadId) {
    t2 = (threadId, e) => {
      if (e.shiftKey && lastClickedThreadId) {
        const allIds = allThreadsInOrder.map(_temp2);
        const startIdx = allIds.indexOf(lastClickedThreadId);
        const endIdx = allIds.indexOf(threadId);
        if (startIdx !== -1 && endIdx !== -1) {
          const rangeStart = Math.min(startIdx, endIdx);
          const rangeEnd = Math.max(startIdx, endIdx);
          const rangeIds = allIds.slice(rangeStart, rangeEnd + 1);
          setMultiSelectedIds(prev => {
            const newSet = new Set(prev);
            rangeIds.forEach(id_1 => newSet.add(id_1));
            return newSet;
          });
        }
      } else {
        if (e.ctrlKey || e.metaKey) {
          setMultiSelectedIds(prev_0 => {
            const newSet_0 = new Set(prev_0);
            if (newSet_0.has(threadId)) {
              newSet_0.delete(threadId);
            } else {
              newSet_0.add(threadId);
            }
            return newSet_0;
          });
          setLastClickedThreadId(threadId);
        }
      }
    };
    $[5] = allThreadsInOrder;
    $[6] = lastClickedThreadId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleThreadMultiSelect = t2;
  let t3;
  if ($[8] !== onSelectThread) {
    t3 = threadId_0 => {
      setMultiSelectedIds(new Set());
      setLastClickedThreadId(threadId_0);
      onSelectThread(threadId_0);
    };
    $[8] = onSelectThread;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleThreadSelect = t3;
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      activationConstraint: {
        distance: 8
      }
    };
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const sensors = useSensors(useSensor(PointerSensor, t4));
  let t5;
  let t6;
  if ($[11] !== isCreatingFolder) {
    t5 = () => {
      if (isCreatingFolder && newFolderInputRef.current) {
        newFolderInputRef.current.focus();
      }
    };
    t6 = [isCreatingFolder];
    $[11] = isCreatingFolder;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = event => {
      const {
        active
      } = event;
      const itemType = active.data.current?.type as "folder" | "thread";
      setActiveItem({
        type: itemType,
        id: active.id as string
      });
    };
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleDragStart = t7;
  let t8;
  if ($[15] !== folders || $[16] !== multiSelectedIds) {
    t8 = event_0 => {
      const {
        over,
        active: active_0
      } = event_0;
      const newOverId = over?.id as string | null;
      setOverId(newOverId);
      const activeType = active_0.data.current?.type as "folder" | "thread";
      const overType = over?.data.current?.type as "folder" | "thread" | "root" | undefined;
      const getMousePosition = () => {
        const activatorEvent = event_0.activatorEvent as MouseEvent | TouchEvent;
        let x = 0;
        let y = 0;
        if (activatorEvent instanceof MouseEvent) {
          x = activatorEvent.clientX;
          y = activatorEvent.clientY;
        } else {
          if (activatorEvent instanceof TouchEvent && activatorEvent.touches[0]) {
            x = activatorEvent.touches[0].clientX;
            y = activatorEvent.touches[0].clientY;
          }
        }
        if (event_0.delta) {
          x = x + event_0.delta.x;
          y = y + event_0.delta.y;
        }
        return {
          x,
          y
        };
      };
      if (activeType === "thread" && overType === "folder" && newOverId) {
        const folderName = findFolderName(newOverId, folders);
        if (folderName) {
          const {
            x: x_0,
            y: y_0
          } = getMousePosition();
          const activeThreadId = active_0.id as string;
          const isDraggingMultiple = multiSelectedIds.has(activeThreadId) && multiSelectedIds.size > 1;
          const displayName = isDraggingMultiple ? `${multiSelectedIds.size} threads` : folderName;
          setDropIndicator({
            folderName: displayName,
            x: x_0,
            y: y_0,
            count: isDraggingMultiple ? multiSelectedIds.size : 1,
            itemType: "thread"
          });
        } else {
          setDropIndicator(null);
        }
      } else {
        if (activeType === "folder" && overType === "folder" && newOverId && active_0.id !== newOverId) {
          const activeFolderId = active_0.id as string;
          if (isDescendantOf(newOverId, activeFolderId, folders)) {
            setDropIndicator(null);
            return;
          }
          const targetFolderName = findFolderName(newOverId, folders);
          const sourceFolderName = findFolderName(activeFolderId, folders);
          if (targetFolderName && sourceFolderName) {
            const {
              x: x_1,
              y: y_1
            } = getMousePosition();
            setDropIndicator({
              folderName: targetFolderName,
              x: x_1,
              y: y_1,
              itemType: "folder"
            });
          } else {
            setDropIndicator(null);
          }
        } else {
          if (overType === "root" || newOverId === "root") {
            setDropIndicator(null);
          } else {
            setDropIndicator(null);
          }
        }
      }
    };
    $[15] = folders;
    $[16] = multiSelectedIds;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleDragOver = t8;
  let t9;
  if ($[18] !== folders || $[19] !== multiSelectedIds || $[20] !== onBulkMoveThreads || $[21] !== onMoveFolder || $[22] !== onMoveThread) {
    t9 = async event_1 => {
      const {
        active: active_1,
        over: over_0
      } = event_1;
      setActiveItem(null);
      setOverId(null);
      setDropIndicator(null);
      if (!over_0) {
        return;
      }
      const activeType_0 = active_1.data.current?.type as "folder" | "thread";
      const overType_0 = over_0.data.current?.type as "folder" | "thread" | "root";
      const targetFolderId = over_0.id as string;
      const isRootTarget = overType_0 === "root" || over_0.id === "root";
      if (activeType_0 === "thread") {
        const activeThreadId_0 = active_1.id as string;
        const isPartOfMultiSelect = multiSelectedIds.has(activeThreadId_0) && multiSelectedIds.size > 1;
        if (overType_0 === "folder") {
          if (isPartOfMultiSelect) {
            await onBulkMoveThreads(Array.from(multiSelectedIds), targetFolderId);
            setMultiSelectedIds(new Set());
          } else {
            await onMoveThread(activeThreadId_0, targetFolderId);
          }
        } else {
          if (isRootTarget) {
            if (isPartOfMultiSelect) {
              await onBulkMoveThreads(Array.from(multiSelectedIds), null);
              setMultiSelectedIds(new Set());
            } else {
              await onMoveThread(activeThreadId_0, null);
            }
          }
        }
      } else {
        if (activeType_0 === "folder") {
          const activeFolderId_0 = active_1.id as string;
          if (overType_0 === "folder" && active_1.id !== over_0.id) {
            if (isDescendantOf(targetFolderId, activeFolderId_0, folders)) {
              return;
            }
            await onMoveFolder(activeFolderId_0, targetFolderId);
          } else {
            if (isRootTarget) {
              await onMoveFolder(activeFolderId_0, null);
            }
          }
        }
      }
    };
    $[18] = folders;
    $[19] = multiSelectedIds;
    $[20] = onBulkMoveThreads;
    $[21] = onMoveFolder;
    $[22] = onMoveThread;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleDragEnd = t9;
  let t10;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = async t11 => {
      const parentId = t11 === undefined ? null : t11;
      setNewFolderParentId(parentId);
      setIsCreatingFolder(true);
      setNewFolderName("");
    };
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const handleCreateFolder = t10;
  let t11;
  if ($[25] !== newFolderName || $[26] !== newFolderParentId || $[27] !== onCreateFolder) {
    t11 = async () => {
      const trimmedName = newFolderName.trim();
      if (!trimmedName) {
        setIsCreatingFolder(false);
        return;
      }
      await onCreateFolder(trimmedName, newFolderParentId);
      setIsCreatingFolder(false);
      setNewFolderName("");
      setNewFolderParentId(null);
    };
    $[25] = newFolderName;
    $[26] = newFolderParentId;
    $[27] = onCreateFolder;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const handleSaveNewFolder = t11;
  let t12;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      setIsCreatingFolder(false);
      setNewFolderName("");
      setNewFolderParentId(null);
    };
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const handleCancelNewFolder = t12;
  let t13;
  if ($[30] !== handleSaveNewFolder) {
    t13 = e_1 => {
      if (e_1.key === "Enter") {
        e_1.preventDefault();
        handleSaveNewFolder();
      } else {
        if (e_1.key === "Escape") {
          handleCancelNewFolder();
        }
      }
    };
    $[30] = handleSaveNewFolder;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const handleNewFolderKeyDown = t13;
  let t14;
  if ($[32] !== folders || $[33] !== threads) {
    t14 = () => {
      const ids = [];
      const collectIds = folder_3 => {
        ids.push(folder_3.id);
        folder_3.threads.forEach(t_2 => ids.push(t_2.id));
        folder_3.children.forEach(collectIds);
      };
      folders.forEach(collectIds);
      threads.forEach(t_3 => ids.push(t_3.id));
      return ids;
    };
    $[32] = folders;
    $[33] = threads;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const getAllIds = t14;
  const findActiveFolder = (id_2, folders_0) => {
    for (const folder_4 of folders_0) {
      if (folder_4.id === id_2) {
        return folder_4;
      }
      const found_1 = findActiveFolder(id_2, folder_4.children);
      if (found_1) {
        return found_1;
      }
    }
    return null;
  };
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      id: "root",
      data: {
        type: "root"
      }
    };
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const {
    setNodeRef: setRootRef
  } = useDroppable(t15);
  const findActiveThread = _temp3;
  const T0 = DndContext;
  const t16 = pointerWithin;
  let t17;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => handleCreateFolder(null);
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <button type="button" onClick={t17} className="mb-2 flex items-center gap-2 px-2 py-1.5 text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 rounded-md transition-colors"><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>+ New Folder</button>;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== handleNewFolderKeyDown || $[39] !== handleSaveNewFolder || $[40] !== isCreatingFolder || $[41] !== newFolderName || $[42] !== newFolderParentId) {
    t19 = isCreatingFolder && newFolderParentId === null && <div className="mb-2 flex items-center gap-1 px-2 py-1"><svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg><input ref={newFolderInputRef} type="text" value={newFolderName} onChange={e_2 => setNewFolderName(e_2.target.value)} onKeyDown={handleNewFolderKeyDown} onBlur={handleSaveNewFolder} placeholder="Directory name" className="flex-1 rounded border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-slate-100 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500" /></div>;
    $[38] = handleNewFolderKeyDown;
    $[39] = handleSaveNewFolder;
    $[40] = isCreatingFolder;
    $[41] = newFolderName;
    $[42] = newFolderParentId;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== getAllIds) {
    t20 = getAllIds();
    $[44] = getAllIds;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== folders || $[47] !== handleNewFolderKeyDown || $[48] !== handleSaveNewFolder || $[49] !== handleThreadMultiSelect || $[50] !== handleThreadSelect || $[51] !== isCreatingFolder || $[52] !== multiSelectedIds || $[53] !== newFolderName || $[54] !== newFolderParentId || $[55] !== onAddThreadToContext || $[56] !== onDeleteFolder || $[57] !== onDeleteThread || $[58] !== onShowThreadInfo || $[59] !== onToggleFolderCollapse || $[60] !== onUpdateFolder || $[61] !== onUpdateThreadTitle || $[62] !== overId || $[63] !== selectedThreadId || $[64] !== threadContextIds) {
    t21 = folders.map(folder_6 => <FolderItem key={folder_6.id} folder={folder_6} depth={0} selectedThreadId={selectedThreadId} onSelectThread={handleThreadSelect} onDeleteThread={onDeleteThread} onUpdateThreadTitle={onUpdateThreadTitle} onUpdateFolder={onUpdateFolder} onDeleteFolder={onDeleteFolder} onToggleFolderCollapse={onToggleFolderCollapse} onCreateSubfolder={handleCreateFolder} isOver={overId === folder_6.id} isCreatingSubfolder={isCreatingFolder && newFolderParentId === folder_6.id} newFolderName={newFolderName} onNewFolderNameChange={setNewFolderName} onNewFolderKeyDown={handleNewFolderKeyDown} onNewFolderBlur={handleSaveNewFolder} newFolderInputRef={newFolderInputRef} multiSelectedIds={multiSelectedIds} onThreadMultiSelect={handleThreadMultiSelect} threadContextIds={threadContextIds} onAddThreadToContext={onAddThreadToContext} onShowThreadInfo={onShowThreadInfo} />);
    $[46] = folders;
    $[47] = handleNewFolderKeyDown;
    $[48] = handleSaveNewFolder;
    $[49] = handleThreadMultiSelect;
    $[50] = handleThreadSelect;
    $[51] = isCreatingFolder;
    $[52] = multiSelectedIds;
    $[53] = newFolderName;
    $[54] = newFolderParentId;
    $[55] = onAddThreadToContext;
    $[56] = onDeleteFolder;
    $[57] = onDeleteThread;
    $[58] = onShowThreadInfo;
    $[59] = onToggleFolderCollapse;
    $[60] = onUpdateFolder;
    $[61] = onUpdateThreadTitle;
    $[62] = overId;
    $[63] = selectedThreadId;
    $[64] = threadContextIds;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  let t22;
  if ($[66] !== handleThreadMultiSelect || $[67] !== handleThreadSelect || $[68] !== multiSelectedIds || $[69] !== onAddThreadToContext || $[70] !== onDeleteThread || $[71] !== onShowThreadInfo || $[72] !== onUpdateThreadTitle || $[73] !== selectedThreadId || $[74] !== threadContextIds || $[75] !== threads) {
    let t23;
    if ($[77] !== handleThreadMultiSelect || $[78] !== handleThreadSelect || $[79] !== multiSelectedIds || $[80] !== onAddThreadToContext || $[81] !== onDeleteThread || $[82] !== onShowThreadInfo || $[83] !== onUpdateThreadTitle || $[84] !== selectedThreadId || $[85] !== threadContextIds) {
      t23 = thread_0 => <ThreadItem key={thread_0.id} thread={thread_0} isSelected={thread_0.id === selectedThreadId} isMultiSelected={multiSelectedIds.has(thread_0.id)} isInContext={threadContextIds?.has(thread_0.id) ?? false} onSelect={() => handleThreadSelect(thread_0.id)} onMultiSelect={e_3 => handleThreadMultiSelect(thread_0.id, e_3)} onDelete={() => onDeleteThread(thread_0.id)} onUpdateTitle={newTitle => onUpdateThreadTitle(thread_0.id, newTitle)} onAddToContext={onAddThreadToContext ? () => onAddThreadToContext(thread_0.id, thread_0.title || "Untitled") : undefined} onShowInfo={onShowThreadInfo ? () => onShowThreadInfo(thread_0.id) : undefined} depth={0} />;
      $[77] = handleThreadMultiSelect;
      $[78] = handleThreadSelect;
      $[79] = multiSelectedIds;
      $[80] = onAddThreadToContext;
      $[81] = onDeleteThread;
      $[82] = onShowThreadInfo;
      $[83] = onUpdateThreadTitle;
      $[84] = selectedThreadId;
      $[85] = threadContextIds;
      $[86] = t23;
    } else {
      t23 = $[86];
    }
    t22 = threads.map(t23);
    $[66] = handleThreadMultiSelect;
    $[67] = handleThreadSelect;
    $[68] = multiSelectedIds;
    $[69] = onAddThreadToContext;
    $[70] = onDeleteThread;
    $[71] = onShowThreadInfo;
    $[72] = onUpdateThreadTitle;
    $[73] = selectedThreadId;
    $[74] = threadContextIds;
    $[75] = threads;
    $[76] = t22;
  } else {
    t22 = $[76];
  }
  let t23;
  if ($[87] !== t20 || $[88] !== t21 || $[89] !== t22) {
    t23 = <SortableContext items={t20} strategy={verticalListSortingStrategy}>{t21}{t22}</SortableContext>;
    $[87] = t20;
    $[88] = t21;
    $[89] = t22;
    $[90] = t23;
  } else {
    t23 = $[90];
  }
  let t24;
  if ($[91] !== folders.length || $[92] !== isCreatingFolder || $[93] !== threads.length) {
    t24 = folders.length === 0 && threads.length === 0 && !isCreatingFolder && <div className="px-2 py-3 text-xs text-slate-500 text-center">No threads yet. Create one to get started.</div>;
    $[91] = folders.length;
    $[92] = isCreatingFolder;
    $[93] = threads.length;
    $[94] = t24;
  } else {
    t24 = $[94];
  }
  let t25;
  if ($[95] !== setRootRef || $[96] !== t19 || $[97] !== t23 || $[98] !== t24) {
    t25 = <div ref={setRootRef} className="flex flex-col min-h-[200px]">{t18}{t19}{t23}{t24}</div>;
    $[95] = setRootRef;
    $[96] = t19;
    $[97] = t23;
    $[98] = t24;
    $[99] = t25;
  } else {
    t25 = $[99];
  }
  const T1 = DragOverlay;
  const t26 = activeItem && activeItem.type === "folder" && <div className="rounded-md bg-slate-800 border border-slate-600 px-3 py-2 text-sm text-slate-200 shadow-lg"><div className="flex items-center gap-2"><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>{findActiveFolder(activeItem.id, folders)?.name || "Directory"}</div></div>;
  let t27;
  if ($[100] !== activeItem || $[101] !== findActiveThread || $[102] !== folders || $[103] !== multiSelectedIds || $[104] !== threads) {
    t27 = activeItem && activeItem.type === "thread" && <div className="rounded-md bg-slate-800 border border-slate-600 px-3 py-2 text-sm text-slate-200 shadow-lg">{multiSelectedIds.has(activeItem.id) && multiSelectedIds.size > 1 ? <div className="flex items-center gap-2"><span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-500 text-xs font-bold">{multiSelectedIds.size}</span><span>{multiSelectedIds.size} threads selected</span></div> : findActiveThread(activeItem.id, folders, threads)?.title || "Thread"}</div>;
    $[100] = activeItem;
    $[101] = findActiveThread;
    $[102] = folders;
    $[103] = multiSelectedIds;
    $[104] = threads;
    $[105] = t27;
  } else {
    t27 = $[105];
  }
  let t28;
  if ($[106] !== T1 || $[107] !== t26 || $[108] !== t27) {
    t28 = <T1>{t26}{t27}</T1>;
    $[106] = T1;
    $[107] = t26;
    $[108] = t27;
    $[109] = t28;
  } else {
    t28 = $[109];
  }
  let t29;
  if ($[110] !== dropIndicator) {
    t29 = dropIndicator && <div className="fixed z-[9999] pointer-events-none px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium shadow-lg whitespace-nowrap" style={{
      left: dropIndicator.x + 20,
      top: dropIndicator.y - 10
    }}><div className="flex items-center gap-1.5">{dropIndicator.itemType === "folder" ? <><svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>Move into "{dropIndicator.folderName}"</> : <><svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>{dropIndicator.count && dropIndicator.count > 1 ? `Move ${dropIndicator.count} threads to "${dropIndicator.folderName}"` : `Add to "${dropIndicator.folderName}"`}</>}</div></div>;
    $[110] = dropIndicator;
    $[111] = t29;
  } else {
    t29 = $[111];
  }
  let t30;
  if ($[112] !== T0 || $[113] !== handleDragEnd || $[114] !== handleDragOver || $[115] !== sensors || $[116] !== t16 || $[117] !== t25 || $[118] !== t28 || $[119] !== t29) {
    t30 = <T0 sensors={sensors} collisionDetection={t16} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>{t25}{t28}{t29}</T0>;
    $[112] = T0;
    $[113] = handleDragEnd;
    $[114] = handleDragOver;
    $[115] = sensors;
    $[116] = t16;
    $[117] = t25;
    $[118] = t28;
    $[119] = t29;
    $[120] = t30;
  } else {
    t30 = $[120];
  }
  return t30;
}
function _temp3(id_3, folders_1, rootThreads) {
  const rootThread = rootThreads.find(t_4 => t_4.id === id_3);
  if (rootThread) {
    return rootThread;
  }
  const searchFolders = folders_2 => {
    for (const folder_5 of folders_2) {
      const thread = folder_5.threads.find(t_5 => t_5.id === id_3);
      if (thread) {
        return thread;
      }
      const found_2 = searchFolders(folder_5.children);
      if (found_2) {
        return found_2;
      }
    }
    return null;
  };
  return searchFolders(folders_1);
}
function _temp2(t_1) {
  return t_1.id;
}
function _temp(targetId, folderId, folderList_0) {
  const findFolder = (id_0, list) => {
    for (const folder_1 of list) {
      if (folder_1.id === id_0) {
        return folder_1;
      }
      const found_0 = findFolder(id_0, folder_1.children);
      if (found_0) {
        return found_0;
      }
    }
    return null;
  };
  const sourceFolder = findFolder(folderId, folderList_0);
  if (!sourceFolder) {
    return false;
  }
  const checkDescendants = folder_2 => {
    if (folder_2.id === targetId) {
      return true;
    }
    return folder_2.children.some(checkDescendants);
  };
  return checkDescendants(sourceFolder);
}